import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, TrendingUp, TrendingDown, AlertTriangle,
  ArrowLeft, Plus, Camera, CheckCircle, BarChart3, Target,
  Loader2, Trash2, Calendar, BookOpen, X, Edit3, Image
} from 'lucide-react';

const subjectIcons = { 
  Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', 
  Hindi: '📚', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' 
};

export default function SchoolScoreTracker({ onNavigate }) {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('scores');
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadMode, setUploadMode] = useState('upload'); // 'upload' or 'manual'
  const fileInputRef = useRef(null);
  
  const [manualEntry, setManualEntry] = useState({
    subject: '',
    test_name: '',
    marks_obtained: '',
    total_marks: '',
    date: new Date().toISOString().split('T')[0]
  });

  const fetchData = useCallback(async () => {
    try {
      const [scoresRes, gapsRes] = await Promise.all([
        api.get('/parent/school-scores'),
        api.get('/parent/gap-analysis')
      ]);
      setScores(scoresRes.data);
      setGaps(gapsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post('/parent/upload-report-card', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      console.error('Failed to upload:', err);
      alert('Failed to process report card. Please try again or add scores manually.');
    }
    setUploading(false);
  };

  const handleManualAdd = async () => {
    if (!manualEntry.subject || !manualEntry.marks_obtained || !manualEntry.total_marks) {
      alert('Please fill all required fields');
      return;
    }

    setUploading(true);
    try {
      await api.post('/parent/add-school-score', {
        ...manualEntry,
        marks_obtained: parseFloat(manualEntry.marks_obtained),
        total_marks: parseFloat(manualEntry.total_marks)
      });
      
      setShowAddModal(false);
      setManualEntry({
        subject: '',
        test_name: '',
        marks_obtained: '',
        total_marks: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (err) {
      console.error('Failed to add score:', err);
    }
    setUploading(false);
  };

  const handleDeleteScore = async (scoreId) => {
    if (!window.confirm('Delete this score?')) return;
    
    try {
      await api.delete(`/parent/school-scores/${scoreId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Calculate overall stats
  const avgScore = scores.length > 0 
    ? Math.round(scores.reduce((acc, s) => acc + (s.marks_obtained / s.total_marks) * 100, 0) / scores.length)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center mx-auto mb-3 animate-pulse">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="school-score-tracker">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button 
            onClick={() => onNavigate('parent-dashboard')} 
            className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-slate-400" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">School Scores</h1>
            <p className="text-xs text-slate-500">Track & compare performance</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center hover:bg-violet-500 transition-all"
            data-testid="add-score-btn"
          >
            <Plus className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* Stats Overview */}
        {scores.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{scores.length}</p>
              <p className="text-xs text-slate-500">Tests</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${avgScore >= 70 ? 'text-emerald-400' : avgScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {avgScore}%
              </p>
              <p className="text-xs text-slate-500">Average</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-violet-400">{gaps.length}</p>
              <p className="text-xs text-slate-500">Gaps Found</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 p-1 bg-slate-900/50 rounded-xl mb-6">
          <button
            onClick={() => setActiveTab('scores')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
              activeTab === 'scores' 
                ? 'bg-violet-600 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="scores-tab"
          >
            <BarChart3 className="w-4 h-4" />
            Scores
          </button>
          <button
            onClick={() => setActiveTab('gaps')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
              activeTab === 'gaps' 
                ? 'bg-violet-600 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="gaps-tab"
          >
            <Target className="w-4 h-4" />
            Gap Analysis
          </button>
        </div>

        {/* Scores Tab */}
        {activeTab === 'scores' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {scores.length > 0 ? (
              <div className="space-y-3">
                {scores.map((score, i) => {
                  const percentage = Math.round((score.marks_obtained / score.total_marks) * 100);
                  const colorClass = percentage >= 80 ? 'emerald' : percentage >= 60 ? 'amber' : 'red';
                  
                  return (
                    <motion.div
                      key={score.id || i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
                      data-testid={`score-card-${i}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center text-xl">
                            {subjectIcons[score.subject] || '📚'}
                          </div>
                          <div>
                            <p className="text-white font-semibold">{score.subject}</p>
                            <p className="text-xs text-slate-500">{score.test_name || 'School Test'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold text-${colorClass}-400`}>
                            {percentage}%
                          </p>
                          <p className="text-xs text-slate-500">{score.marks_obtained}/{score.total_marks}</p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-3">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.5, delay: i * 0.1 }}
                          className={`h-full rounded-full bg-${colorClass}-500`}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(score.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <button
                          onClick={() => handleDeleteScore(score.id)}
                          className="text-slate-600 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-slate-600" />
                </div>
                <p className="text-white font-medium mb-1">No scores yet</p>
                <p className="text-sm text-slate-500 mb-4">Upload a report card or add scores manually</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-violet-600 hover:bg-violet-500 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-all"
                >
                  Add First Score
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Gap Analysis Tab */}
        {activeTab === 'gaps' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Info Banner */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-300 font-medium">Gap Detection</p>
                  <p className="text-xs text-slate-400 mt-1">
                    We compare school test scores with StudyClaw practice performance to identify concept gaps.
                  </p>
                </div>
              </div>
            </div>

            {gaps.length > 0 ? (
              <div className="space-y-3">
                {gaps.map((gap, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`bg-slate-900/50 border rounded-xl p-5 ${
                      gap.gap_size > 20 ? 'border-red-500/30' : 'border-amber-500/30'
                    }`}
                    data-testid={`gap-card-${i}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center text-xl">
                          {subjectIcons[gap.subject] || '📚'}
                        </div>
                        <div>
                          <p className="text-white font-semibold">{gap.subject}</p>
                          <p className="text-xs text-slate-500">{gap.topic || 'Overall'}</p>
                        </div>
                      </div>
                      <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                        gap.gap_size > 20 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {gap.gap_size > 20 ? '🔴 Critical' : '🟡 Needs Work'}
                      </div>
                    </div>

                    {/* Score Comparison */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 mb-1">School Score</p>
                        <div className="flex items-end gap-1">
                          <p className="text-2xl font-bold text-white">{gap.school_score}</p>
                          <p className="text-slate-500 text-sm mb-0.5">%</p>
                        </div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 mb-1">Practice Score</p>
                        <div className="flex items-end gap-1">
                          <p className="text-2xl font-bold text-cyan-400">{gap.practice_score}</p>
                          <p className="text-slate-500 text-sm mb-0.5">%</p>
                        </div>
                      </div>
                    </div>

                    {/* Gap Indicator */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {gap.school_score > gap.practice_score ? (
                          <TrendingDown className="w-5 h-5 text-red-400" />
                        ) : (
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                        )}
                        <span className={`text-sm font-medium ${
                          gap.school_score > gap.practice_score ? 'text-red-400' : 'text-emerald-400'
                        }`}>
                          {Math.abs(gap.gap_size)}% gap
                        </span>
                      </div>
                    </div>

                    {gap.insight && (
                      <div className="bg-slate-800/30 rounded-lg p-3 mb-4">
                        <p className="text-xs text-slate-400">💡 {gap.insight}</p>
                      </div>
                    )}

                    <button
                      onClick={() => onNavigate('daily-loop', { subject: gap.subject })}
                      className="w-full bg-violet-600 hover:bg-violet-500 text-white py-3 rounded-xl text-sm font-semibold transition-all"
                    >
                      Practice {gap.subject} Now →
                    </button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="text-white font-medium mb-1">No gaps detected!</p>
                <p className="text-sm text-slate-500">Add more school scores to compare with practice performance</p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Add Score Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 w-full max-w-lg rounded-t-3xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-800">
                <h2 className="text-lg font-bold text-white">Add Score</h2>
                <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mode Toggle */}
              <div className="p-5 border-b border-slate-800">
                <div className="flex gap-2 p-1 bg-slate-800/50 rounded-xl">
                  <button
                    onClick={() => setUploadMode('upload')}
                    className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      uploadMode === 'upload' ? 'bg-violet-600 text-white' : 'text-slate-400'
                    }`}
                  >
                    <Image className="w-4 h-4" />
                    Upload Marksheet
                  </button>
                  <button
                    onClick={() => setUploadMode('manual')}
                    className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      uploadMode === 'manual' ? 'bg-violet-600 text-white' : 'text-slate-400'
                    }`}
                  >
                    <Edit3 className="w-4 h-4" />
                    Enter Manually
                  </button>
                </div>
              </div>

              <div className="p-5 max-h-[60vh] overflow-y-auto">
                {uploadMode === 'upload' ? (
                  /* Upload Mode */
                  <div className="text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*,.pdf"
                      className="hidden"
                    />
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-700 hover:border-violet-500/50 rounded-2xl p-8 cursor-pointer transition-all"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-12 h-12 text-violet-400 mx-auto mb-3 animate-spin" />
                          <p className="text-white font-medium">Processing...</p>
                          <p className="text-xs text-slate-500 mt-1">Extracting scores with AI</p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
                            <Camera className="w-8 h-8 text-violet-400" />
                          </div>
                          <p className="text-white font-medium mb-1">Upload Report Card</p>
                          <p className="text-sm text-slate-500 mb-4">Take a photo or select from gallery</p>
                          <div className="flex items-center justify-center gap-2 text-xs text-slate-600">
                            <span>📷 Photo</span>
                            <span>•</span>
                            <span>📄 PDF</span>
                            <span>•</span>
                            <span>🖼️ Image</span>
                          </div>
                        </>
                      )}
                    </div>

                    <p className="text-xs text-slate-500 mt-4">
                      AI will automatically extract subject-wise scores from your marksheet
                    </p>
                  </div>
                ) : (
                  /* Manual Entry Mode */
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Subject *</label>
                      <select
                        value={manualEntry.subject}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-white focus:border-violet-500 outline-none"
                      >
                        <option value="">Select subject</option>
                        {['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology', 'EVS'].map(s => (
                          <option key={s} value={s}>{subjectIcons[s]} {s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Test Name</label>
                      <input
                        type="text"
                        value={manualEntry.test_name}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, test_name: e.target.value }))}
                        placeholder="e.g., Unit Test 1, Half Yearly"
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-white placeholder-slate-500 focus:border-violet-500 outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">Marks Obtained *</label>
                        <input
                          type="number"
                          value={manualEntry.marks_obtained}
                          onChange={(e) => setManualEntry(prev => ({ ...prev, marks_obtained: e.target.value }))}
                          placeholder="85"
                          className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-white placeholder-slate-500 focus:border-violet-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">Total Marks *</label>
                        <input
                          type="number"
                          value={manualEntry.total_marks}
                          onChange={(e) => setManualEntry(prev => ({ ...prev, total_marks: e.target.value }))}
                          placeholder="100"
                          className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-white placeholder-slate-500 focus:border-violet-500 outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Date</label>
                      <input
                        type="date"
                        value={manualEntry.date}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3.5 text-white focus:border-violet-500 outline-none"
                      />
                    </div>

                    <button
                      onClick={handleManualAdd}
                      disabled={uploading || !manualEntry.subject || !manualEntry.marks_obtained || !manualEntry.total_marks}
                      className="w-full bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-xl py-3.5 font-semibold transition-all"
                    >
                      {uploading ? 'Adding...' : 'Add Score'}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
